import { create } from 'zustand';
import type { BrandConfig } from '../types/brand.types';
import { loadBrandConfig, getBrandImageUrl } from '../utils/brandLoader';
import { appConfig } from '../config/app-config';
import { logError } from '../utils/rollbar';

interface BrandStore {
  // State
  brandId: string;
  config: BrandConfig | null;
  loading: boolean;
  error: string | null;

  // Actions
  loadBrand: (brandId: string) => Promise<void>;
  getImageUrl: (imageName: string) => string;
  applyBrandColors: () => void;
  reset: () => void;
}

export const useBrandStore = create<BrandStore>((set, get) => ({
  // Initial state
  brandId: appConfig.defaultBrandId,
  config: null,
  loading: false,
  error: null,

  // Load brand configuration
  loadBrand: async (brandId: string) => {
    set({ loading: true, error: null, brandId });

    try {
      const config = await loadBrandConfig(brandId);
      set({ config, loading: false });

      // Apply colors to CSS variables
      get().applyBrandColors();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load brand config';
      logError('Error in loadBrand:', error);
      set({ error: errorMessage, loading: false });

      // Still try to apply any loaded config
      get().applyBrandColors();
    }
  },

  // Get full URL for a brand image
  getImageUrl: (imageName: string) => {
    return getBrandImageUrl(imageName);
  },

  // Apply brand colors to CSS custom properties
  applyBrandColors: () => {
    const { config } = get();

    if (!config) {
      console.warn('No brand config available to apply colors');
      return;
    }

    const root = document.documentElement;

    // Apply primary and secondary colors
    if (config.primaryColor) {
      root.style.setProperty('--primary-color', config.primaryColor);
      root.style.setProperty('--radcliffe-primary', config.primaryColor);
      root.style.setProperty('--border-color-focus', config.primaryColor);
      root.style.setProperty('--radcliffe-accent', config.primaryColor);
    }

    if (config.secondaryColor) {
      root.style.setProperty('--primary-color-hover', config.secondaryColor);
      root.style.setProperty('--radcliffe-primary-dark', config.secondaryColor);
    }

    // Apply hero header customizations
    // Use heroBackgroundColor if provided, otherwise use secondaryColor (darker shade)
    const heroBackground = config.heroBackgroundColor || config.secondaryColor;
    if (heroBackground) {
      root.style.setProperty('--hero-background-color', heroBackground);
    }

    if (config.images?.heroBanner) {
      const heroBannerUrl = getBrandImageUrl(config.images.heroBanner);
      root.style.setProperty('--hero-background-image', `url('${heroBannerUrl}')`);
    }

    // Update favicon if provided
    if (config.images?.favicon) {
      const faviconUrl = getBrandImageUrl(config.images.favicon);
      let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;

      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      
      link.href = faviconUrl;
    }

    // Note: Tertiary color is not applied to keep original grey background for questions
  },

  // Reset to default state
  reset: () => {
    set({
      brandId: appConfig.defaultBrandId,
      config: null,
      loading: false,
      error: null
    });
  }
}));

// Selector hooks for convenience
export const useBrandConfig = () => useBrandStore((state) => state.config);
export const useBrandLoading = () => useBrandStore((state) => state.loading);
export const useBrandError = () => useBrandStore((state) => state.error);
export const useLoadBrand = () => useBrandStore((state) => state.loadBrand);
export const useBrandImageUrl = () => useBrandStore((state) => state.getImageUrl);
